import {useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {HelpIcon} from '@/components/help-icon'
import {EvidenciaLigada} from '@/components/descargar-evidencia'
import {
  EVIDENCE_ACCEPT,
  EVIDENCE_ALLOWED_TYPES,
  EVIDENCE_MAX_BYTES,
  evidenceTooBig,
} from '@/hooks/sgsst/evidencias/use-evidence'
import {useUploadOtraEvidence, type Matriz} from '@/hooks/sgsst/riesgos/use-ipevr'
import {translateApiError} from '@/lib/errors'

export function OtraMetodologiaPanel({
  businessId,
  matriz,
  canEdit,
}: {
  businessId: string
  matriz: Matriz
  canEdit: boolean
}) {
  const {t} = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const upload = useUploadOtraEvidence(businessId)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  function handleFile(files: FileList | null) {
    const file = files?.[0]
    if (!file) return
    if (!EVIDENCE_ALLOWED_TYPES.has(file.type)) {
      setError(t('errors.evidenceBadFormat'))
      return
    }
    if (file.size > EVIDENCE_MAX_BYTES) {
      setError(evidenceTooBig(t, file))
      return
    }
    setError(null)
    setProgress(0)
    upload.mutate({file, onProgress: setProgress}, {onError: (err) => setError(translateApiError(err))})
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className="surface space-y-3 px-4 py-4" data-testid="ipevr-otra-metodologia">
      <div className="flex items-center gap-1">
        <h2 className="text-sm font-semibold text-foreground">{t('ipevr.otra.title')}</h2>
        <HelpIcon label={t('ipevr.tipLabel.otra')} testId="ipevr-otra-help">
          {t('ipevr.otra.tip')}
        </HelpIcon>
      </div>

      {matriz.otra_evidence ? (
        <EvidenciaLigada
          businessId={businessId}
          evidencia={matriz.otra_evidence}
          testId="ipevr-otra-evidence-file"
          className="flex items-center gap-2 text-sm font-medium text-primary hover:underline"
        />
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="ipevr-otra-empty">
          {t('ipevr.otra.empty')}
        </p>
      )}

      {canEdit && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={EVIDENCE_ACCEPT}
            hidden
            data-testid="ipevr-otra-input"
            onChange={(e) => handleFile(e.target.files)}
          />
          <Button size="sm" variant="outline" disabled={upload.isPending} onClick={() => inputRef.current?.click()}>
            {matriz.otra_evidence ? t('ipevr.otra.replace') : t('ipevr.otra.upload')}
          </Button>
          {upload.isPending && (
            <div className="h-1.5 w-40 overflow-hidden rounded-full bg-secondary">
              <div className="h-full rounded-full bg-primary transition-all" style={{width: `${progress}%`}} />
            </div>
          )}
          {error && (
            <p className="text-xs text-destructive" data-testid="ipevr-otra-error">
              {error}
            </p>
          )}
        </>
      )}
    </div>
  )
}
